import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { UsersIcon, BuildingIcon, ShieldIcon, WalletIcon, ChevronRightIcon, FileIcon, ClockIcon, BellIcon } from '../components/Icons';

const NAT_COLORS = ['#fb866d','#1a2744','#22c55e','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#84cc16'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/employees/stats'), api.get('/employees/alerts')])
      .then(([s, a]) => { setStats(s.data); setAlerts(a.data.slice(0, 6)); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh] text-slate-400 text-sm gap-3">
      <div className="w-5 h-5 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
      Loading dashboard...
    </div>
  );

  const maxP = stats?.projectBreakdown?.[0]?.[1] || 1;
  const maxN = stats?.nationalityBreakdown?.[0]?.[1] || 1;
  const dot = { expired: 'bg-red-500', critical: 'bg-[#fb866d]', warning: 'bg-amber-400' };
  const badge = { expired: 'bg-red-50 text-red-600', critical: 'bg-orange-50 text-[#e8614a]', warning: 'bg-amber-50 text-amber-700' };

  const heroStats = [
    { icon: UsersIcon, val: stats?.active ?? 0, label: 'Employees', sub: 'active' },
    { icon: BuildingIcon, val: stats?.clients ?? 0, label: 'Clients', sub: 'active sites' },
    { icon: WalletIcon, val: `${((stats?.totalPayroll ?? 0) / 1000).toFixed(0)}K`, label: 'Payroll', sub: 'SAR / month' },
    { icon: WalletIcon, val: `${(stats?.totalDeductions ?? 0).toLocaleString()}`, label: 'Deductions', sub: 'SAR due', accent: (stats?.totalDeductions ?? 0) > 0 },
  ];

  const compliance = stats?.complianceByType || {};
  const complianceStats = [
    { icon: ShieldIcon, val: compliance.iqama ?? 0, label: 'Iqama', link: true },
    { icon: FileIcon, val: compliance.qiwa ?? 0, label: 'Qiwa', link: true },
    { icon: FileIcon, val: compliance.passport ?? 0, label: 'Passport', link: true },
    { icon: BellIcon, val: compliance.insurance ?? 0, label: 'Insurance', link: true },
    { icon: ClockIcon, val: compliance.leave ?? 0, label: 'Leave', link: true },
  ].map(t => ({ ...t, sub: t.val > 0 ? 'alerts' : 'clear', accent: t.val > 0 }));

  const allTiles = [...heroStats, ...complianceStats];

  return (
    <div>
      {/* Topbar */}
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Dashboard</h1>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
        </div>
      </div>

      <div className="p-4 lg:p-8 stagger">
        {/* Hero banner */}
        <div className="rounded-2xl p-5 lg:p-7 mb-6 relative overflow-hidden"
          style={{ background: 'linear-gradient(120deg, #0f1b33 0%, #1a2744 45%, #2d3f6b 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 75% 30%, #fb866d 0%, transparent 55%)' }} />
          <div className="relative flex items-start justify-between mb-6">
            <div>
              <h2 className="text-white text-xl font-bold">Delta Manpower — HR Console</h2>
              <p className="text-slate-400 text-sm mt-0.5">
                Workforce Dashboard · {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="relative grid grid-cols-3 sm:grid-cols-5 xl:grid-cols-9 gap-2 lg:gap-2.5">
            {allTiles.map(({ icon: Icon, val, label, sub, accent, link }) => (
              <button key={label} onClick={() => link && navigate('/compliance')}
                className={`rounded-xl px-3 py-3.5 backdrop-blur-sm text-left transition-all ${link ? 'hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'}`}
                style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${accent ? 'rgba(251,134,109,0.35)' : 'rgba(255,255,255,0.09)'}` }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon size={20} className={accent ? 'text-[#fb866d]' : 'text-slate-400'} />
                  <span className={`text-xl font-bold leading-none ${accent ? 'text-[#fb866d]' : 'text-white'}`}>{val}</span>
                </div>
                <p className="text-white text-[11px] font-semibold leading-tight whitespace-nowrap">{label}</p>
                <p className={`text-[9.5px] mt-0.5 ${accent ? 'text-[#fb866d]/80' : 'text-slate-500'}`}>{sub}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
            <p className="text-[14px] font-bold text-slate-800 mb-1">Headcount by Client Site</p>
            <p className="text-xs text-slate-400 mb-5">Active employees per project</p>
            <div className="space-y-3">
              {(stats?.projectBreakdown || []).slice(0, 8).map(([proj, count]) => (
                <div key={proj} className="flex items-center gap-3">
                  <span className="text-[12.5px] text-slate-500 w-32 truncate flex-shrink-0" title={proj}>{proj}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${(count / maxP) * 100}%`, background: 'linear-gradient(90deg, #fb866d, #e8614a)' }} />
                  </div>
                  <span className="text-[12.5px] font-bold text-slate-700 w-7 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
            <p className="text-[14px] font-bold text-slate-800 mb-1">Nationality Breakdown</p>
            <p className="text-xs text-slate-400 mb-5">Active workforce composition</p>
            <div className="space-y-3">
              {(stats?.nationalityBreakdown || []).slice(0, 7).map(([nat, count], i) => (
                <div key={nat} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: NAT_COLORS[i % NAT_COLORS.length] }} />
                  <span className="text-[12.5px] text-slate-500 w-28 truncate flex-shrink-0">{nat}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(count / maxN) * 100}%`, background: NAT_COLORS[i % NAT_COLORS.length] }} />
                  </div>
                  <span className="text-[12.5px] font-bold text-slate-700 w-7 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Secondary row: sponsorship + alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="bg-white rounded-2xl border border-slate-100 p-6" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
            <p className="text-[14px] font-bold text-slate-800 mb-1">By Sponsorship</p>
            <p className="text-xs text-slate-400 mb-5">Active employees</p>
            <div className="space-y-3.5">
              {(stats?.sponsorshipBreakdown || []).slice(0, 6).map(([sp, count], i) => (
                <div key={sp} className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: NAT_COLORS[i % NAT_COLORS.length] }} />
                    <span className="text-[13px] text-slate-600 truncate">{sp}</span>
                  </div>
                  <span className="text-[13px] font-bold text-slate-800">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-50">
              <div>
                <p className="text-[14px] font-bold text-slate-800">Document Expiry Alerts</p>
                <p className="text-xs text-slate-400 mt-0.5">Iqama · Qiwa · Passport — most urgent first</p>
              </div>
              <button onClick={() => navigate('/compliance')}
                className="flex items-center gap-1 text-xs font-semibold text-[#e8614a] px-3.5 py-2 rounded-full border border-[#fb866d]/30 hover:bg-[#fde8e3] transition-all">
                View All <ChevronRightIcon size={14} />
              </button>
            </div>
            {alerts.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">No pending alerts</div>
            ) : alerts.map(e => (
              <div key={`${e.id}-${e.alertType}`} className="flex items-center gap-3 px-6 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dot[e.level]}`} />
                <span className="text-[13px] font-medium text-slate-800 flex-1 truncate">{e.name}</span>
                <span className="text-xs text-slate-400 hidden md:block">{e.project}</span>
                <span className="text-xs text-slate-400 hidden lg:block">{e.sponsorship || '—'}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{e.alertType}</span>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge[e.level]}`}>
                  {e.days < 0 ? `Expired ${Math.abs(e.days)}d ago` : `${e.days}d left`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}